import {useCallback, useRef, useState} from 'react';
import {RemoveScroll} from 'react-remove-scroll';

import {Check} from '../../assets/Check';
import {SelectItem} from '../../SelectModal';
import styles from './Modal.module.scss';

type Props<T = string | number> = {
  values: Array<SelectItem<T>>;
  onChangeValue: (item: SelectItem<T>, index: number) => void;
  currentValue: SelectItem<T>;
  onCloseModal: () => void;
};

const PERCENTAGE_FOR_CLOSE = 10;

export const Modal = <T,>({
  values,
  onChangeValue,
  currentValue,
  onCloseModal,
}: Props<T>) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const isOpened = useRef(false);
  const [touchStartY, setTouchStartY] = useState(0);
  const onOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      triggerCloseAnimation();
    }
  };
  const onSelect = (item: SelectItem<T>, index: number) => {
    onChangeValue(item, index);
    triggerCloseAnimation();
  };
  const triggerCloseAnimation = () => {
    modalRef.current?.classList.add(styles.closeAnimation);
  };

  const onAnimationEnd = useCallback(
    (event: React.AnimationEvent<HTMLDivElement>) => {
      //На первое срабатывание - анимация "модалка открылась". На второе - закрыть модалку.
      if (!isOpened.current) {
        return (isOpened.current = true);
      }
      return onCloseModal();
    },
    [onChangeValue],
  );

  //Для мобильной логики закрытия

  const onTouchMove = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      if (modalRef.current) {
        modalRef.current.style.transition = 'none';
        const currentY = event.touches[0].clientY;
        if (!touchStartY) {
          setTouchStartY(currentY);
        }
        if (touchStartY && currentY > touchStartY) {
          modalRef.current.style.transform = `translate3d(0, ${currentY - touchStartY}px, 0)`;
        }
      }
    },
    [touchStartY],
  );

  const onTouchEnd = useCallback(
    (event: React.TouchEvent<HTMLDivElement>) => {
      const tounchEndY = event.changedTouches[0].clientY;
      const scrolledFromStart = (touchStartY / tounchEndY) * 100;
      const isMustBeClosed =
        100 - Math.floor(scrolledFromStart) > PERCENTAGE_FOR_CLOSE;

      if (modalRef.current && touchStartY) {
        if (!isMustBeClosed) {
          //Если закрытие было жиденьким, и провели вниз слишком мало, то возвращаем обратно в 0 состояние
          modalRef.current.style.transition = 'transform .1s linear';
          modalRef.current.style.transform = `translate3d(0, 0px, 0)`;
        } else {
          triggerCloseAnimation();
        }
      }
      setTouchStartY(0);
    },
    [touchStartY],
  );
  return (
    <RemoveScroll>
      <div
        className={styles.overlay}
        onClick={onOverlayClick}
        data-testid={'modalOverlay'}
      />

      <div
        className={styles.selectModal}
        ref={modalRef}
        onAnimationEnd={onAnimationEnd}
        data-testid="modal"
      >
        <div
          className={styles.mobileMoveLine}
          onTouchEnd={onTouchEnd}
          onTouchMove={onTouchMove}
        />
        {values.map(({title, description, value}, index) => (
          <span
            className={styles.selectModal__item}
            onClick={() => onSelect({title, description, value}, index)}
            key={index}
            data-testid={`value-${value}`}
            data-active={value === currentValue.value ? 'active' : 'unactive'}
          >
            {title}
            {description && (
              <span className={styles.selectModal__item_description}>
                {description}
              </span>
            )}
            {value === currentValue.value && <Check />}
          </span>
        ))}
      </div>
    </RemoveScroll>
  );
};
